import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { MapStatusChip } from './MappingShared';
import { compactDialogTableSx } from './VendingStyles';
import vendingApiClient from '../../Services/VendingApi';
import { COLORS } from '../../Styles/Colors';
import { AutoMapPreviewCandidate, AutoMapPreviewResponse } from '../../Types/vending';

interface ProductMappingAutoMapDialogProps {
  open: boolean;
  machineId: string;
  machineLabel?: string;
  onClose: () => void;
  onApplied: (mappedCount: number) => void;
}

export const ProductMappingAutoMapDialog = ({
  open,
  machineId,
  machineLabel,
  onClose,
  onApplied,
}: ProductMappingAutoMapDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<AutoMapPreviewCandidate[]>([]);
  const [conflictCount, setConflictCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const loadPreview = useCallback(async () => {
    if (!machineId) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ machine_id: machineId });
      const response = await vendingApiClient(`/mappings/products/auto-map/preview?${params.toString()}`);
      const json: AutoMapPreviewResponse = await response.json();
      const list = json.data?.candidates ?? [];
      setCandidates(list);
      setConflictCount(json.data?.conflict_count ?? 0);
      setSelectedIds(new Set(list.map((c) => c.id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load auto-map preview');
      setCandidates([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    if (open && machineId) {
      loadPreview();
    } else {
      setCandidates([]);
      setSelectedIds(new Set());
      setError(null);
    }
  }, [open, machineId, loadPreview]);

  const selectedCount = selectedIds.size;
  const allSelected = candidates.length > 0 && selectedCount === candidates.length;

  const selectedConflicts = useMemo(
    () => candidates.filter((c) => selectedIds.has(c.id) && c.would_conflict).length,
    [candidates, selectedIds],
  );

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(candidates.map((c) => c.id)));
    }
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (selectedCount === 0) return;
    setApplying(true);
    setError(null);
    try {
      const response = await vendingApiClient('/mappings/products/auto-map', {
        method: 'POST',
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      const json = await response.json();
      onApplied(json.data?.mapped ?? selectedCount);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Auto-map failed');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Confirm auto-map
        {machineLabel && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
            {machineLabel}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
            <CircularProgress size={28} sx={{ color: COLORS.teal }} />
            <Typography variant="body2" color="text.secondary">Finding exact matches…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>
        )}

        {!loading && !error && candidates.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No exact matches for this machine.
          </Typography>
        )}

        {!loading && candidates.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {candidates.length} match{candidates.length === 1 ? '' : 'es'}
              {conflictCount > 0 && ` · ${conflictCount} conflict${conflictCount === 1 ? '' : 's'}`}
            </Typography>

            <TableContainer sx={{ maxHeight: 380, border: `1px solid ${COLORS.lightGray}40`, borderRadius: 1 }}>
              <Table stickyHeader size="small" sx={compactDialogTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedCount > 0 && !allSelected}
                        onChange={toggleAll}
                        size="small"
                        sx={{ p: 0.25 }}
                      />
                    </TableCell>
                    <TableCell>Vendor product</TableCell>
                    <TableCell>Item ID</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell>AIMS code</TableCell>
                    <TableCell>AIMS name</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((candidate) => {
                    const isSelected = selectedIds.has(candidate.id);
                    return (
                      <TableRow
                        key={candidate.id}
                        hover
                        selected={isSelected}
                        onClick={() => toggleOne(candidate.id)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleOne(candidate.id)}
                            size="small"
                            sx={{ p: 0.25 }}
                          />
                        </TableCell>
                        <TableCell>{candidate.vendor_name || '—'}</TableCell>
                        <TableCell>{candidate.provider_item_id || '—'}</TableCell>
                        <TableCell>{candidate.provider_sku || '—'}</TableCell>
                        <TableCell>
                          {candidate.match_field === 'product_code' ? 'Item ID' : 'SKU'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: isSelected ? 600 : 400 }}>
                          {candidate.aims_product.product_code || candidate.aims_product.sku || '—'}
                        </TableCell>
                        <TableCell>{candidate.aims_product.name || '—'}</TableCell>
                        <TableCell>
                          {candidate.would_conflict
                            ? <MapStatusChip status="conflict" />
                            : <Typography component="span" sx={{ color: COLORS.lightGray, fontSize: '0.75rem' }}>—</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedConflicts > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {selectedConflicts} selected will be saved as conflict — AIMS product already linked elsewhere.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={applying} size="small" sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleConfirm}
          disabled={loading || applying || selectedCount === 0}
          sx={{
            backgroundColor: COLORS.teal,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { backgroundColor: COLORS.darkTeal, boxShadow: 'none' },
          }}
        >
          {applying ? 'Mapping…' : `Confirm ${selectedCount}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductMappingAutoMapDialog;
